<template>
  <div
    v-if="showModal && !allStepsCompleted"
    class="grid grid-cols-12 gap-4 mb-[43px] rounded-2xl border border-brand-175 bg-brand-150 shadow-[0_4px_8px_0_rgba(15,125,139,0.10)] p-6 mt-8"
  >
    <div class="col-span-12 flex justify-end">
      <X @click="dismissModal" class="text-grey-light-400 font-medium cursor-pointer" />
    </div>
    <div class="col-span-12 flex items-center justify-between">
      <div>
        <h2 class="text-lg font-semibold mb-1">{{ headingText }}</h2>
        <p class="text-grey-light-600 font-medium">
          {{ subtitleText }}
        </p>
      </div>
      <div class="hidden md:block">
        <Button type="button" @click="navigateTo">{{ buttonLabel }}</Button>
      </div>
    </div>
    <div class="col-span-12 pb-2">
      <div class="relative h-2 w-full overflow-hidden rounded-full bg-grey-light-200 my-3">
        <div class="h-full bg-brand-600 transition-all rounded-full" :style="{ width: progressPercentage }" />
      </div>
      <div>
        <span class="text-[14px] text-brand-600 font-medium">
          {{ completedSteps }}/{{ totalSteps }} Steps completed
        </span>
      </div>
      <div class="block md:hidden pt-5">
        <Button type="button" @click="navigateTo">{{ buttonLabel }}</Button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { getCards } from '@/services/cards';
import { useOrderStore } from '@/stores/orders';
import { useUserStore } from '@/stores/users';
import { X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { ModelCardResponse } from '@layup/api-sdk';

const router = useRouter();
const userStore = useUserStore();
const orderStore = useOrderStore();
const { allOrders } = storeToRefs(orderStore);
const { user, hasRequiredPersonalDetails, hasLoggedInBefore } = storeToRefs(userStore);

const cards = ref<ModelCardResponse[]>([]);

onMounted(async () => {
  try {
    cards.value = await getCards();
  } catch {
    // silently fail — don't block the banner
  }
});

const DISMISS_KEY = 'completeStepsCardDismissed';
const showModal = ref(!sessionStorage.getItem(DISMISS_KEY));

const requiredSteps = computed(() => [
  hasRequiredPersonalDetails.value,
  !!(user.value.bankDetails?.verified || cards?.value?.length > 0),
  allOrders.value.length > 0,
]);

const totalSteps = computed(() => requiredSteps.value?.length ?? 0);

const completedSteps = computed(() => requiredSteps.value?.filter((item) => !!item)?.length ?? 0);

const allStepsCompleted = computed(() => completedSteps.value === totalSteps.value);

const progressPercentage = computed(() => `${(completedSteps.value / totalSteps.value) * 100}%`);

const currentStep = computed(() => {
  if (completedSteps.value === 0 || !hasRequiredPersonalDetails.value) return 'profile';
  else if (!user.value.bankDetails?.verified && cards?.value?.length === 0) return 'bank';
  else return 'order';
});

const stepConfig = {
  profile: {
    subtitle: "To get the best experience possible, let's complete setting up your profile.",
    buttonLabel: 'Complete profile',
    route: { name: 'shopperProfile', query: { source: 'progress_strip' } },
  },
  bank: {
    subtitle: "Almost there! Let's add your banking details so refunds and payouts are sorted.",
    buttonLabel: 'Load bank details',
    route: { name: 'shopperProfile', query: { tab: 'payment', source: 'progress_strip' } },
  },
  order: {
    subtitle: "You're all set! Let's get you started with your first plan.",
    buttonLabel: 'Create plan',
    route: { name: 'shopperStores', query: { source: 'progress_strip' } },
  },
};

const subtitleText = computed(() => stepConfig[currentStep.value].subtitle);
const buttonLabel = computed(() => stepConfig[currentStep.value].buttonLabel);
const headingText = computed(() => (hasLoggedInBefore.value ? 'Welcome back to LayUp!' : 'Welcome to LayUp!'));

const navigateTo = () => router.push(stepConfig[currentStep.value].route);

const dismissModal = () => {
  sessionStorage.setItem(DISMISS_KEY, 'true');
  showModal.value = false;
};
</script>
